import { redirect, useLoaderData, useSearchParams } from 'react-router';
import { Result } from 'ts-results-es';
import { z } from 'zod';
import { zx } from 'zodix';

import { AthletesSection } from '~/components/home-page/athletes-section';
import { GymOwnersSection } from '~/components/home-page/gym-owners-section';
import { HeaderSection } from '~/components/home-page/header-section';
import { Footer } from '~/components/layout/footer';
import { PublicNavbar } from '~/components/layout/public-navbar';
import { getAthleteProfileIdForUserId } from '~/domain/athlete-profiles/queries/get-athlete-profile-id-for-user-id.server';
import { getCities } from '~/domain/shared/queries/get-cities.server';
import { getEventCities } from '~/domain/shared/queries/get-event-cities.server';
import { getEventZipCodes } from '~/domain/shared/queries/get-event-zip-codes.server';
import { getZipCodes } from '~/domain/shared/queries/get-zip-codes.server';
import { getSessionId, hasPermission, requireSession } from '~/utils/auth.server.ts';
import { handleDomainError } from '~/utils/errors.server';

import { type Route } from './+types/_index';

export async function loader({ request }: Route.ActionArgs) {
  const sessionId = await getSessionId(request);

  const { search, city } = zx.parseQuery(request, {
    search: z.string().trim().optional(),
    city: z.string().trim().optional(),
  });

  // Not logged in - continue to unauthenticated page if they don't have a session
  if (sessionId == null) {
    if (search || city) {
      const { searchParams } = new URL(request.url);
      return redirect(`/gym?${searchParams}`);
    }

    const [citiesResult, zipCodesResult, eventCitiesResult, eventZipCodesResult] = await Promise.all(
      [getCities(), getZipCodes(), getEventCities(), getEventZipCodes()],
    );
    const result = Result.all(citiesResult, zipCodesResult, eventCitiesResult, eventZipCodesResult);

    return result
      .map(([cities, zipCodes, eventCities, eventZipCodes]) => ({
        cities,
        zipCodes,
        eventCities,
        eventZipCodes,
      }))
      .mapErr(handleDomainError)
      .unwrap();
  }

  // Logged in - hasPermission forces a sign in, so we're saving it for later
  if (await hasPermission(request, ['FP.Dashboard'])) {
    return redirect('/admin/dashboard');
  }

  if (await hasPermission(request, ['GymList'])) {
    return redirect('/admin/gyms');
  }

  // go to gym search if they have a session and search Params, otherwise go to athlete profile
  const { searchParams } = new URL(request.url);
  if (![...searchParams.entries()].length) {
    const session = await requireSession(request);
    const athleteProfileId = (await getAthleteProfileIdForUserId(session.userId)) as string;
    return redirect(`/a/${athleteProfileId}/training/`);
  } else {
    return redirect(`/gym?${searchParams}`);
  }
}

export default function Index() {
  // there won't be any, but it keeps the search component happy
  const [searchParams] = useSearchParams();
  const { cities, zipCodes, eventCities, eventZipCodes } = useLoaderData<typeof loader>();

  const comboboxItems = [
    ...cities!.map(({ city, state }) => `${city}, ${state}`),
    ...zipCodes!.map((z) => z?.toString() ?? ''),
  ].filter((d) => d !== '');

  const eventComboboxItems = [
    ...eventCities!.map(({ city, state }) => `${city}, ${state}`),
    ...eventZipCodes!.map((z) => z?.toString() ?? ''),
  ].filter((d) => d !== '');

  // sticky footer based on https://developer.mozilla.org/en-US/docs/Web/CSS/Layout_cookbook/Sticky_footers
  return (
    <div className="flex min-h-screen flex-col">
      <PublicNavbar />
      <HeaderSection
        comboboxItems={comboboxItems}
        eventComboboxItems={eventComboboxItems}
        searchParams={searchParams}
      />
      <GymOwnersSection />
      <AthletesSection />
      <Footer />
    </div>
  );
}
